import { useState } from "react";
import { FaFilePdf, FaFileExcel, FaGoogleDrive } from "react-icons/fa";
import useDragAndDrop from "./useDragAndDrop";
import axios from "axios";
import { useCookies } from "react-cookie";
import { toast } from "react-toastify";
import configuration from "../config";

const Dragger = ({ category, subCategory, fileType, kycId, onFileUpload }) => {
  const { dragOver, onDragOver, onDragLeave, setDragOver } = useDragAndDrop();
  const [uploading, setUploading] = useState(false);
  const [selectedFiles, setSelectedFiles] = useState([]);
  const [progress, setProgress] = useState(0);
  const [cookies] = useCookies(["token"]);
  const { API_BASE_URL } = configuration;

  const uploadFiles = async (files) => {
    setUploading(true);
    setProgress(0);
    const formData = new FormData();
    
    files.forEach(file => {
      formData.append("file", file);
    });
    
    formData.append("category", category);
    formData.append("subCategory", subCategory);
    formData.append("fileType", fileType);
    formData.append("kycId", kycId);

    try {
      const response = await axios.post(
        `${API_BASE_URL}/api/v1/uploadClientFiles/${kycId}`,
        formData,
        {
          headers: {
            "Content-Type": "multipart/form-data",
            Authorization: `Bearer ${cookies.token}`,
          },
          onUploadProgress: (progressEvent) => {
            const progress = Math.round(
              (progressEvent.loaded / progressEvent.total) * 100
            );
            setProgress(progress);
          },
        }
      );

      if (response.data.success) {
        toast.success(`Files uploaded successfully!`);

        if (response.data.files && response.data.files.length > 0) {
          response.data.files.forEach(file => {
            onFileUpload(category, subCategory, fileType, file);
          });
        } else if (response.data.file) {
          onFileUpload(category, subCategory, fileType, response.data.file);
        } else {
          files.forEach(file => {
            onFileUpload(category, subCategory, fileType, { name: file.name });
          });
        }

        setSelectedFiles([]);
      } else {
        throw new Error(response.data.message || "File upload failed");
      }
    } catch (error) {
      console.error("Error uploading file:", error);
      toast.error(
        error.response?.data?.message ||
          error.message ||
          "Error uploading file. Please try again."
      );
    } finally {
      setUploading(false);
      setProgress(0);
    }
  };

  const onDrop = (e) => {
    e.preventDefault();
    setDragOver(false);
    const files = Array.from(e.dataTransfer.files);
    if (files.length > 0) setSelectedFiles(files);
  };

  const onChange = (e) => {
    const files = Array.from(e.target.files);
    if (files.length > 0) setSelectedFiles(files);
  };

  const uploadButtonHandler = () => {
    if (selectedFiles.length > 0) {
      uploadFiles(selectedFiles);
    }
  };

  const renderIcon = () => {
    switch (fileType.toLowerCase()) {
      case "pdf":
      case "report":
      case "docs":
        return <FaFilePdf className="me-2" />;
      case "excel":
      case "monthly":
      case "yearly":
      case "quarterly":
      case "weekly":
        return <FaFileExcel className="me-2" />;
      default:
        return <FaGoogleDrive className="me-2" />;
    }
  };

  return (
    <div
      className={`d-flex flex-column align-items-center justify-content-center mb-3 ${
        dragOver ? "border-primary" : ""
      }`}
      style={{ 
        minHeight: "150px",
        cursor: "pointer", 
        width: "30%",
        backgroundColor: dragOver ? "#f8f9fa" : "#EB5A3C",
        color: dragOver ? "#333" : "#fff",
        borderRadius: "12px",
        border: dragOver ? "2px dashed #EB5A3C" : "2px solid #EB5A3C",
        padding: "20px",
        transition: "all 0.3s ease",
        boxShadow: "0 4px 6px rgba(0, 0, 0, 0.1)"
      }}
      onDragOver={onDragOver}
      onDragLeave={onDragLeave}
      onDrop={onDrop}
    >
      {selectedFiles.length === 0 ? (
        <label
          htmlFor={`file-upload-${category}-${subCategory}-${fileType}`}
          style={{ 
            cursor: "pointer", 
            display: "flex", 
            flexDirection: "column",
            alignItems: "center",
            justifyContent: "center",
            width: "100%",
            height: "100%"
          }}
        >
          <div style={{ fontSize: "32px", marginBottom: "10px" }}>
            {renderIcon()}
          </div>
          <span style={{ fontSize: "16px", fontWeight: "500", textAlign: "center" }}>
            {uploading ? "Uploading..." : `Upload ${fileType}`}
          </span>
          <span style={{ fontSize: "12px", opacity: "0.8", marginTop: "5px" }}>
            Drag & drop or click to browse
          </span>
        </label>
      ) : (
        <div style={{ width: "100%", display: "flex", flexDirection: "column", gap: "10px" }}>
          <div style={{ 
            fontSize: "14px", 
            fontWeight: "600", 
            marginBottom: "5px",
            color: "#fff"
          }}>
            {selectedFiles.length} file{selectedFiles.length > 1 ? 's' : ''} selected
          </div>
          <div style={{ 
            maxHeight: "100px", 
            overflowY: "auto",
            backgroundColor: "rgba(255, 255, 255, 0.1)",
            borderRadius: "6px",
            padding: "8px"
          }}>
            {selectedFiles.map((file, index) => (
              <div 
                key={index} 
                style={{
                  fontSize: "12px",
                  padding: "4px 0",
                  whiteSpace: "nowrap",
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                  color: "#fff"
                }}
              >
                • {file.name}
              </div>
            ))}
          </div>
          <button
            type="button"
            onClick={uploadButtonHandler}
            disabled={uploading}
            style={{
              padding: "10px 20px",
              backgroundColor: uploading ? "#ccc" : "#fff",
              color: uploading ? "#666" : "#EB5A3C",
              border: "none",
              borderRadius: "8px",
              cursor: uploading ? "not-allowed" : "pointer",
              transition: "all 0.3s ease",
              fontSize: "14px",
              fontWeight: "600",
              boxShadow: uploading
                ? "none"
                : "0 2px 8px rgba(0, 0, 0, 0.15)",
              marginTop: "5px"
            }}
            onMouseOver={(e) => {
              if (!uploading) {
                e.currentTarget.style.backgroundColor = "#f8f9fa";
                e.currentTarget.style.transform = "translateY(-2px)";
              }
            }}
            onMouseOut={(e) => {
              if (!uploading) {
                e.currentTarget.style.backgroundColor = "#fff";
                e.currentTarget.style.transform = "translateY(0)";
              }
            }}
          >
            {uploading ? "Uploading..." : `Upload ${selectedFiles.length} file${selectedFiles.length > 1 ? 's' : ''}`}
          </button>
        </div>
      )}
      <input
        id={`file-upload-${category}-${subCategory}-${fileType}`}
        type="file"
        multiple
        onChange={onChange}
        style={{ display: "none" }}
        disabled={uploading}
      />
      {uploading && (
        <div style={{ 
          marginTop: "15px", 
          fontSize: "14px",
          fontWeight: "500",
          width: "100%"
        }}>
          <div style={{ marginBottom: "5px" }}>Upload Progress: {progress}%</div>
          <div style={{
            width: "100%",
            height: "6px",
            backgroundColor: "rgba(255, 255, 255, 0.3)",
            borderRadius: "3px",
            overflow: "hidden"
          }}>
            <div style={{
              width: `${progress}%`,
              height: "100%",
              backgroundColor: "#fff",
              transition: "width 0.3s ease"
            }}></div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Dragger;
